/**
 * Shared footer rendered inside every MarketingLayout-wrapped page.
 * @file src/web/shared/components/Footer.tsx
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import { useBranding } from '../hooks/useBranding';
import { FresherbotLogo } from './FresherbotLogo';

const INK  = 'oklch(0.20 0.012 270)';
const INK2 = 'oklch(0.46 0.010 270)';
const INK3 = 'oklch(0.62 0.008 270)';
const LINE = 'oklch(0.91 0.006 270)';
const BG   = 'oklch(0.985 0.003 270)';

function BackToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      type="button"
      aria-label="Scroll to top"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-6 right-6 z-40 size-10 rounded-full shadow-lg hover:opacity-90 transition-opacity flex items-center justify-center"
      style={{ background: INK, color: 'white' }}
    >
      <ArrowUp className="h-4 w-4" />
    </button>
  );
}

export const Footer: React.FC = () => {
  const year = new Date().getFullYear();
  const { name, loaded } = useBranding();
  // Don't flash the FresherBot marketing footer before branding resolves, and
  // never render it on a white-label deployment (testmug / partner brand set).
  if (!loaded || name) return null;

  return (
    <>
      <footer style={{ borderTop: `1px solid ${LINE}`, background: BG }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">

          {/* Top grid: brand + product + company */}
          <div className="footer-grid">

            {/* Brand */}
            <div>
              <FresherbotLogo height={22} />
              <p className="mt-3 text-xs" style={{ color: INK3 }}>Placement intelligence for India's TPOs.</p>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: INK2 }}>
                Intelligent shortlisting, structured preparation, and real-time placement analytics — built for Training and Placement Officers across Indian universities.
              </p>
              <a
                href="mailto:info@fresherbot.com"
                className="mt-4 inline-block text-sm hover:underline"
                style={{ color: INK3 }}
              >
                info@fresherbot.com
              </a>
            </div>

            {/* Product */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: INK3 }}>
                Product
              </div>
              <div className="space-y-3">
                {[
                  { label: 'For Placement Officers', to: '/placement-officer' },
                  { label: 'For Students', to: '/for-students' },
                  { label: 'Exam Cover Browser', to: '/exam-cover' },
                  { label: 'Request Access', to: '/waitlist' },
                  { label: 'NPEI Initiative', to: '/npei' },
                  { label: 'Sign In', to: '/auth/login' },
                ].map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="block text-sm transition-colors hover:underline"
                    style={{ color: INK2 }}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Company */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: INK3 }}>
                Company
              </div>
              <div className="space-y-3">
                {[
                  { label: 'About', to: '/about' },
                  { label: 'Contact', to: '/contact' },
                  { label: 'Security', to: '/security' },
                ].map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="block text-sm transition-colors hover:underline"
                    style={{ color: INK2 }}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Resources */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: INK3 }}>
                Resources
              </div>
              <div className="space-y-3">
                <Link to="/blog" className="block text-sm transition-colors hover:underline" style={{ color: INK2 }}>
                  Blog
                </Link>
                <Link to="/downloads" className="block text-sm transition-colors hover:underline" style={{ color: INK2 }}>
                  Downloads
                </Link>
                <a
                  href="https://www.linkedin.com/company/fresherbot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm transition-colors hover:underline"
                  style={{ color: INK2 }}
                >
                  LinkedIn
                </a>
              </div>
            </div>
          </div>

          {/* Bottom: copyright + legal */}
          <div
            className="mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4"
            style={{ borderTop: `1px solid ${LINE}` }}
          >
            <p className="text-xs" style={{ color: INK3 }}>
              © {year} FresherBot. All rights reserved.
            </p>
            <nav className="flex flex-wrap justify-center gap-x-5 gap-y-1">
              {[
                { label: 'Terms', to: '/terms' },
                { label: 'Privacy', to: '/privacy' },
                { label: 'Refund', to: '/refund' },
                { label: 'Cancellation', to: '/cancellation' },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="text-xs transition-colors hover:underline"
                  style={{ color: INK3 }}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>

        </div>
      </footer>
      <BackToTopButton />
    </>
  );
};
